import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Insignia, Tarjeta } from './ui/basicos';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { encabezadoDeNota } from './FichaDeSeguimiento';

interface Pendiente {
  readonly id: string;
  readonly terceroId: string;
  readonly razonSocial: string;
  readonly tipo: string;
  readonly texto: string;
  readonly venceEl: string;
  readonly correoDelUsuario: string | null;
  readonly vencido: boolean;
}

/**
 * Qué hay que hacer hoy.
 *
 * ## Por qué está en inicio y no en una pantalla propia
 *
 * Lo decidió el dueño del producto, y es la decisión que hace que el seguimiento sirva: **un
 * pendiente que hay que ir a buscar es un pendiente que no se mira**, y entonces la fecha que
 * alguien anotó era decorativa.
 *
 * ## Por qué no aparece cuando no hay nada
 *
 * Una tarjeta vacía que dice «no hay pendientes» ocupa el mismo lugar todos los días y enseña a no
 * mirar ahí. Cuando no hay nada, no hay tarjeta.
 */
export function QueHacerHoy(): React.ReactElement | null {
  const { t } = useTranslation();
  const cola = useQueryClient();
  const puedeVer = usePermiso('TERCEROS.TERCERO.VER');
  const puedeEditar = usePermiso('TERCEROS.TERCERO.EDITAR');

  const pendientes = useQuery({
    queryKey: ['pendientes'],
    enabled: puedeVer,
    queryFn: () => pedir<Pendiente[]>('/pendientes'),
  });

  const marcarHecho = useMutation({
    mutationFn: (notaId: string) => pedir<void>(`/notas/${notaId}/hecho`, { method: 'POST' }),
    onSuccess: async () => {
      await cola.invalidateQueries({ queryKey: ['pendientes'] });
      await cola.invalidateQueries({ queryKey: ['notas'] });
    },
  });

  const filas = pendientes.data ?? [];
  if (filas.length === 0) return null;

  const vencidos = filas.filter((fila) => fila.vencido).length;

  return (
    <Tarjeta>
      <h2 className="mb-2 text-seccion font-semibold">
        {t('pendientes.titulo')}
        {vencidos === 0 ? null : (
          <span className="ml-2 align-middle">
            <Insignia tono="mal">{t('pendientes.vencidos', { count: vencidos })}</Insignia>
          </span>
        )}
      </h2>
      <ul className="flex flex-col divide-y divide-borde-tenue">
        {filas.map((fila) => (
          <li key={fila.id} className="flex items-center gap-3 py-2 text-cuerpo">
            <span className="min-w-0 flex-1">
              <span className="font-medium">{fila.razonSocial}</span>
              <span className="block text-apoyo text-texto-tenue">
                {encabezadoDeNota(fila.venceEl, t(`seguimiento.tipos.${fila.tipo}`, fila.tipo))}
              </span>
              {fila.texto}
            </span>
            {/* Vencido con su color: la diferencia entre «para hoy» y «se pasó» es la única que
                cambia qué se hace primero. */}
            {fila.vencido ? <Insignia tono="mal">{t('pendientes.vencido')}</Insignia> : null}
            {puedeEditar ? (
              <Boton
                tono="sutil"
                cargando={marcarHecho.isPending}
                onClick={() => {
                  marcarHecho.mutate(fila.id);
                }}
              >
                {t('seguimiento.marcarHecho')}
              </Boton>
            ) : null}
          </li>
        ))}
      </ul>
    </Tarjeta>
  );
}
